// Stub address-validation provider. Zero network, zero cost, zero
// vendor lock-in. Applies a small set of deterministic local
// heuristics that catch the most common typos + casing issues a
// dispatcher would notice during a manual review:
//
//   - Trim whitespace on every field.
//   - Collapse internal runs of whitespace to single spaces.
//   - Uppercase a 2-letter state code (the standard US convention).
//   - Validate US ZIP shape (#####  or  #####-####). Anything else
//     gets flagged as needs_review with the input echoed back so
//     the dispatcher can correct it.
//   - Flag incomplete inputs (missing required fields) WITHOUT
//     marking the result invalid -- partial input mid-typing is
//     a normal state, not an error.
//
// This is the v1 placeholder for the cross-cutting address-validation
// primitive. A real-vendor implementation (Google Places /
// SmartyStreets / USPS / Lob) replaces this module wholesale and
// the facade picks it up via setActiveAddressValidationProvider().
import type { AddressValidationProvider } from "./provider";
import type {
  AddressValidationIssue,
  AddressValidationResult,
  StructuredAddress,
} from "./types";

const US_ZIP = /^\d{5}(-\d{4})?$/;
// US territory + state codes (50 + DC + 5 inhabited territories +
// USPS military codes). Used only for the "is this a recognizable
// US state code?" check; a real provider does this with a vendor
// dataset. Out of scope: Canadian provinces, international.
const US_STATE_CODES = new Set([
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  "DC","AS","GU","MP","PR","VI",
  // USPS military / diplomatic codes
  "AA","AE","AP",
]);

function tidy(s: string | undefined): string {
  if (!s) return "";
  return s.trim().replace(/\s+/g, " ");
}

function differs(a: string | undefined, b: string | undefined): boolean {
  return (a ?? "") !== (b ?? "");
}

/**
 * Normalize one address. Pure function; no I/O. Exposed for unit
 * tests + for callers that want the local-only heuristics regardless
 * of which provider is currently active.
 */
export function stubValidateLocal(
  input: StructuredAddress
): AddressValidationResult {
  const issues: AddressValidationIssue[] = [];

  // 1) Whitespace + casing normalization.
  const normalized: StructuredAddress = {
    street: tidy(input.street),
    city: tidy(input.city),
    state: tidy(input.state).toUpperCase(),
    postal_code: tidy(input.postal_code).toUpperCase(),
  };

  // 2) Completeness check (informational, not blocking).
  const filledCount =
    (normalized.street ? 1 : 0) +
    (normalized.city ? 1 : 0) +
    (normalized.state ? 1 : 0) +
    (normalized.postal_code ? 1 : 0);
  const allEmpty = filledCount === 0;

  if (allEmpty) {
    return {
      status: "incomplete",
      normalized,
      suggestedChange: false,
      issues: [],
      provider: stubProvider.id,
    };
  }

  if (!normalized.street) {
    issues.push({ field: "street", severity: "warn", message: "Street is empty." });
  }
  if (!normalized.city) {
    issues.push({ field: "city", severity: "warn", message: "City is empty." });
  }
  if (!normalized.state) {
    issues.push({ field: "state", severity: "warn", message: "State is empty." });
  }
  if (!normalized.postal_code) {
    issues.push({ field: "postal_code", severity: "warn", message: "Postal code is empty." });
  }

  // 3) State-code shape check (only when present + 2 chars).
  if (normalized.state && normalized.state.length === 2 && !US_STATE_CODES.has(normalized.state)) {
    issues.push({
      field: "state",
      severity: "error",
      message: `"${normalized.state}" is not a recognized US state / territory code.`,
    });
  }

  // 4) ZIP shape check (only when present).
  if (normalized.postal_code && !US_ZIP.test(normalized.postal_code)) {
    issues.push({
      field: "postal_code",
      severity: "error",
      message: `"${normalized.postal_code}" is not a US ZIP shape (##### or #####-####).`,
    });
  }

  const suggestedChange =
    differs(input.street, normalized.street) ||
    differs(input.city, normalized.city) ||
    differs(input.state, normalized.state) ||
    differs(input.postal_code, normalized.postal_code);

  const anyError = issues.some((i) => i.severity === "error");
  const incomplete = filledCount < 4;

  let status: AddressValidationResult["status"];
  if (anyError) {
    status = "invalid";
  } else if (incomplete) {
    status = "incomplete";
  } else if (suggestedChange) {
    status = "needs_review";
  } else {
    status = "ok";
  }

  return {
    status,
    normalized,
    suggestedChange,
    issues,
    provider: stubProvider.id,
  };
}

export const stubProvider: AddressValidationProvider = {
  id: "stub-us-local",
  async validate(input) {
    return stubValidateLocal(input);
  },
};
